// =============================================================================
// walkin-data.ts — read-side queries for the new-booking modal
// =============================================================================
// Pulls the minimum reference data the operator needs to fill the form:
// bookable locations, bookable experiences, slots for a chosen day, and the
// suggested per-party price. start_time_id (needed by the RPC) is resolved
// client-side from (room_id, experience_id, time_of_day) — there's a UNIQUE
// constraint on that triple in booking.start_time.
// =============================================================================
import { supabase } from './supabase';
import { fetchDepartures, type Departure } from './departures';

export interface BookingLocation {
  id: string;
  name: string;
  display_name: string | null;
  // Stripe Terminal reader (tmr_...) configured for this location; null = no
  // POS terminal → the walk-in modal hides "Pinterminal (POS)".
  pos_reader_id: string | null;
}

export interface BookingExperience {
  id: string;
  name: string;
  base_price_cents: number;
  duration_minutes: number;
}

interface RefDataPayload {
  locations: Array<{
    id: string;
    name: string;
    display_name: string | null;
    pos_reader_id?: string | null;
  }>;
  experiences: Array<{
    id: string;
    name: string;
    base_price_cents: number;
    duration_minutes: number;
  }>;
  // location_id → experience ids, most-booked first (trailing 30 days,
  // confirmed/arrived picks). Locations without recent bookings are absent.
  experience_ranking?: Record<string, string[]>;
}

// One-shot fetch via fn_admin_walkin_refdata (SECURITY DEFINER). Authenticated
// operators can't SELECT infra.location / experiences.experiences directly.
//
// includeDisabledLocations=true returns every infra.location row regardless
// of booking_enabled. Setup tabs pass true so an admin can prep schedules
// and experience links on a new venue BEFORE flipping it bookable; the
// walk-in modal and the header location switcher leave the default false.
export async function fetchWalkinRefData(
  includeDisabledLocations: boolean = false,
): Promise<{
  locations: BookingLocation[];
  experiences: BookingExperience[];
  // Popularity ranking per location (see RefDataPayload). Used by the
  // new-booking modal to default the experience when the calendar cell
  // didn't pin one.
  experienceRanking: Record<string, string[]>;
}> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_walkin_refdata', {
      p_include_disabled: includeDisabledLocations,
    });
  if (error) throw error;
  const payload = (data as RefDataPayload | null) ?? {
    locations: [],
    experiences: [],
  };
  return {
    experienceRanking: payload.experience_ranking ?? {},
    locations: (payload.locations ?? []).map((r) => ({
      id: r.id,
      name: r.name,
      display_name: r.display_name,
      pos_reader_id: r.pos_reader_id ?? null,
    })),
    experiences: (payload.experiences ?? []).map((r) => ({
      id: r.id,
      name: r.name,
      base_price_cents: Number(r.base_price_cents ?? 0),
      duration_minutes: Number(r.duration_minutes ?? 20),
    })),
  };
}

// Returns the day's slots for one experience. `booked` is already room-wide
// at source (fn_calendar_departures), so two experiences sharing a room
// see the same occupancy — no client-side aggregation needed.
export async function fetchSlotsForDay(
  date: string,
  experienceId: string,
  locationId: string | null,
): Promise<Departure[]> {
  const loc = locationId && locationId.length > 0 ? locationId : null;
  const all = await fetchDepartures(date, date, { locationId: loc });
  return all.filter((d) => d.experience_id === experienceId);
}

// Resolve the synthetic start_time_id for (room_id, slot_start). Pre-0048 the
// schedule materialised one booking.start_time row per HH:MM and we looked it
// up here; 0048-0052 dropped that table from the resolver path and replaced
// it with a synthetic uuid v5 derived from (room_id, slot_start). The walk-in
// RPC re-derives + tamper-checks this id, so passing the same one back is
// enough — there's no row to look up. _experienceId / _timeOfDay are kept in
// the signature for callsite continuity but unused.
export async function resolveStartTimeId(
  roomId: string,
  _experienceId: string,
  _timeOfDay: string,
  slotStart: string,
): Promise<string | null> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_synth_start_time_id', {
      p_room_id:    roomId,
      p_slot_start: slotStart,
    });
  if (error) throw error;
  return typeof data === 'string' ? data : null;
}

// Suggested per-party total in cents — sum of fn_price_for_slot across the
// chosen slot(s). Used to pre-fill the amount input; operator can override.
export async function suggestPriceCents(
  experienceId: string,
  locationId: string,
  slotStart: string,
  partySize: number,
): Promise<number> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_price_for_slot', {
      p_experience_id: experienceId,
      p_location_id: locationId,
      p_slot_start: slotStart,
    });
  if (error) throw error;
  const perSeat = typeof data === 'number' ? data : 0;
  return perSeat * Math.max(1, partySize);
}
